/**
 * @element qds-numeric-input
 *
 * Numeric, currency and percentage entry. The value property is always
 * `number | null` — empty is null, never 0. Display formatting is
 * presentation only and never feeds back into value.
 *
 * **Do not use qds-numeric-input for identifiers that merely look numeric**
 * — account numbers, CUSIPs, phone numbers. Those are text: they have
 * leading zeros, they are never arithmetic, and thousands separators would
 * corrupt them. Use qds-text-input with a pattern.
 *
 * @attr {number|null} value - The numeric value. Empty is null, not 0.
 *   Canonical string in the attribute (e.g. "1234.56"); number|null on the property.
 * @attr {'number'|'currency'|'percent'} mode - Formatting mode. Default 'number'.
 * @attr {string} currency - ISO 4217 currency code. Default 'USD'. Currency mode only.
 * @attr {string} locale - BCP 47 locale tag. Defaults to document locale.
 * @attr {number} min - Minimum value. Sets rangeUnderflow when violated.
 * @attr {number} max - Maximum value. Sets rangeOverflow when violated.
 * @attr {number} step - Step increment for arrow keys and steppers. Default 1.
 * @attr {number} decimals - Decimal places. Mode default: number unlimited, currency 2, percent 2.
 * @attr {boolean} allow-negative - Allow negative values. Default true.
 * @attr {'minus'|'parens'|'parens-red'} negative-style - Display style for negatives. Default 'minus'. Display only.
 * @attr {boolean} grouping - Show grouping separators in display. Default true.
 * @attr {boolean} steppers - Show increment/decrement buttons. Default false.
 * @attr {'start'|'end'} align - Text alignment in the input. Default 'end'.
 * @attr {string} name - Form field name.
 * @attr {string} label - Label text rendered as a real <label for>.
 * @attr {string} hint - Helper text displayed below the control.
 * @attr {string} error-text - Error message; overrides built-in validation message.
 * @attr {string} placeholder - Placeholder text.
 * @attr {'sm'|'md'|'lg'} size - Control size. Default 'md'.
 * @attr {boolean} disabled - Not focusable, not submitted.
 * @attr {boolean} readonly - Focusable, submitted, not editable.
 * @attr {boolean} required - Required field.
 * @attr {boolean} invalid - Author-forced invalid state.
 * @attr {'blur'|'input'|'submit'} validate-on - Validation timing. Default 'blur'.
 *
 * @csspart container - Outer wrapper.
 * @csspart label - Label element.
 * @csspart required - Required marker span.
 * @csspart wrapper - Control surface wrapper.
 * @csspart input - The input element.
 * @csspart affix-prefix - Prefix affix (currency symbol).
 * @csspart affix-suffix - Suffix affix (currency symbol or percent sign).
 * @csspart stepper-up - Increment stepper button.
 * @csspart stepper-down - Decrement stepper button.
 * @csspart hint - Helper text.
 * @csspart error - Error message.
 *
 * @fires input - On keystroke and stepper press. Read .value (number).
 * @fires change - On value commit (blur or Enter).
 * @fires qds-validity-change - On validity transition.
 *
 * @example
 * <qds-numeric-input mode="currency" currency="USD" value="1234.56" label="Amount"></qds-numeric-input>
 * <qds-numeric-input mode="percent" value="12.5" label="Rate"></qds-numeric-input>
 */

import { loadBaseCSS } from '../shared/button-utils.js';
import {
  attachInternalsSafe,
  ValidityTracker,
  dispatchValidityChange,
  generateControlId,
  isAttrTruthy,
  reflectBoolean,
  parseSize,
  parseValidateOn,
  getFormControlStyles,
} from '../shared/form-control.js';
import type { QdsValidity, QdsControlSize, QdsValidateOn } from '../../types/index';

type NumericMode = 'number' | 'currency' | 'percent';
type NegativeStyle = 'minus' | 'parens' | 'parens-red';

export class QdsNumericInput extends HTMLElement {
  static formAssociated = true;
  static qdsFieldConsumer = true;

  static get observedAttributes(): string[] {
    return [
      'value', 'name', 'label', 'placeholder', 'hint', 'error-text',
      'size', 'disabled', 'readonly', 'required', 'invalid', 'validate-on',
      'mode', 'currency', 'locale', 'min', 'max', 'step', 'decimals',
      'allow-negative', 'negative-style', 'grouping', 'steppers', 'align',
    ];
  }

  // ── Form association ────────────────────────────────────────────────
  private _internals?: ElementInternals;
  private _validity!: ValidityTracker;

  // ── Value state ─────────────────────────────────────────────────────
  private _value: number | null = null;
  private _valueAtFocus: number | null = null;
  private _initialValue: number | null = null;
  private _isFocused = false;
  private _updatingFromAttr = false;

  // ── Validation state ────────────────────────────────────────────────
  private _customValidityMessage = '';
  private _hasShownError = false;
  private _prevValid = true;
  private _prevMessage = '';

  // ── IDs ─────────────────────────────────────────────────────────────
  private _inputId: string;
  private _hintId: string;
  private _errorId: string;

  // ── Text-input carry-over attributes ────────────────────────────────
  private _label = '';
  private _hint = '';
  private _errorText = '';
  private _placeholder = '';
  private _name = '';
  private _size: QdsControlSize = 'md';
  private _disabled = false;
  private _readonly = false;
  private _required = false;
  private _invalid = false;
  private _validateOn: QdsValidateOn = 'blur';

  // ── Numeric-specific attributes ─────────────────────────────────────
  private _mode: NumericMode = 'number';
  private _currency = 'USD';
  private _locale = '';
  private _min: number | null = null;
  private _max: number | null = null;
  private _step = 1;
  private _decimals: number | null = null;
  private _allowNegative = true;
  private _negativeStyle: NegativeStyle = 'minus';
  private _grouping = true;
  private _steppers = false;
  private _align: 'start' | 'end' = 'end';

  // ── Bound event handlers (stable refs for add/removeEventListener) ──
  private _onFocus = (e: FocusEvent): void => {
    this._isFocused = true;
    this._valueAtFocus = this._value;
    this._updateDisplay();
  };

  private _onBlur = (e: FocusEvent): void => {
    this._isFocused = false;
    this._commitFromInput();
    this._dispatchChange();
  };

  private _onInput = (e: Event): void => {
    e.stopPropagation();
    const input = e.target as HTMLInputElement;
    if (input) {
      this._value = this._parseInput(input.value);
      this._setFormValue(this._value === null ? null : String(this._value));
    }
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    if (this._validateOn === 'input') {
      this._hasShownError = true;
      this._validate(true);
    } else if (this._hasShownError) {
      this._validate(true);
    }
  };

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (this._disabled || this._readonly) return;

    const key = e.key;

    // Stepping keys
    if (key === 'ArrowUp') {
      e.preventDefault();
      this._stepValue(e.shiftKey ? 10 : 1);
      return;
    }
    if (key === 'ArrowDown') {
      e.preventDefault();
      this._stepValue(e.shiftKey ? -10 : -1);
      return;
    }
    if (key === 'PageUp') {
      e.preventDefault();
      this._stepValue(10);
      return;
    }
    if (key === 'PageDown') {
      e.preventDefault();
      this._stepValue(-10);
      return;
    }
    if (key === 'Home') {
      if (this._min !== null && this._max !== null) {
        e.preventDefault();
        this._commitStepped(this._min);
      }
      return;
    }
    if (key === 'End') {
      if (this._min !== null && this._max !== null) {
        e.preventDefault();
        this._commitStepped(this._max);
      }
      return;
    }
    if (key === 'Escape') {
      e.preventDefault();
      this._value = this._valueAtFocus;
      this._setFormValue(this._value === null ? null : String(this._value));
      this._updateDisplay();
      this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
      return;
    }
    if (key === 'Enter') {
      e.preventDefault();
      this._commitFromInput();
      this._dispatchChange();
      return;
    }

    // Block disallowed characters
    if (!this._isKeyAllowed(key, e)) {
      e.preventDefault();
    }
  };

  private _onPaste = (e: Event): void => {
    if (this._disabled || this._readonly) {
      e.preventDefault();
      return;
    }

    let pastedText = '';
    try {
      const clipData = (e as unknown as ClipboardEvent).clipboardData;
      pastedText = clipData?.getData('text') ?? '';
    } catch {
      pastedText = '';
    }
    if (!pastedText) return;

    const sanitized = this._sanitizePaste(pastedText);
    if (sanitized === null) {
      e.preventDefault();
      return;
    }

    e.preventDefault();

    const input = this._getInput();
    if (!input) return;

    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const newValue = input.value.slice(0, start) + sanitized + input.value.slice(end);
    input.value = newValue;
    input.setSelectionRange(sanitized.length + start, sanitized.length + start);

    this._value = this._parseInput(newValue);
    this._setFormValue(this._value === null ? null : String(this._value));
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  };

  private _onStepperUp = (e: Event): void => {
    e.preventDefault();
    if (this._disabled || this._readonly) return;
    this._stepValue(1);
    this._getInput()?.focus();
  };

  private _onStepperDown = (e: Event): void => {
    e.preventDefault();
    if (this._disabled || this._readonly) return;
    this._stepValue(-1);
    this._getInput()?.focus();
  };

  // ── Constructor ─────────────────────────────────────────────────────

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    // jsdom 26 returns an ElementInternals object from attachInternals() but
    // does not implement setFormValue or validity on it. Detect that and treat
    // internals as unavailable so the ValidityTracker falls back to QdsValidity.
    const internals = attachInternalsSafe(this);
    this._internals = internals && typeof internals.setFormValue === 'function'
      ? internals
      : undefined;
    this._validity = new ValidityTracker(this._internals);
    loadBaseCSS();
    this._inputId = generateControlId('numeric-input');
    this._hintId = generateControlId('numeric-input-hint');
    this._errorId = generateControlId('numeric-input-error');
  }

  // ── Lifecycle ───────────────────────────────────────────────────────

  connectedCallback(): void {
    if (!this.isConnected) return;
    this._syncPrivateFields();

    // Store initial value for formResetCallback
    const attrVal = this.getAttribute('value');
    this._initialValue = this._parseAttrValue(attrVal);

    // Ensure _value matches the attribute on first connect
    if (this._updatingFromAttr === false && this.hasAttribute('value')) {
      this._value = this._initialValue;
    }

    this._setFormValue(this._value === null ? null : String(this._value));
    this.render();
    this._validate(false);
  }

  disconnectedCallback(): void {
    this._detachListeners();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    if (name === 'value') {
      if (this._updatingFromAttr) return;
      this._updatingFromAttr = true;
      this._value = this._parseAttrValue(newValue);
      this._setFormValue(this._value === null ? null : String(this._value));
      this._updateDisplay();
      this._validate(this._hasShownError);
      this._updatingFromAttr = false;
      return;
    }

    this._syncPrivateFields();

    if (this.isConnected) {
      this.render();
      this._validate(this._hasShownError);
    }
  }

  // ── Form lifecycle ──────────────────────────────────────────────────

  formResetCallback(): void {
    this.value = this._initialValue;
    this._hasShownError = false;
    this.removeAttribute('data-invalid');
    this._validate(false);
    this.render();
  }

  formDisabledCallback(disabled: boolean): void {
    if (disabled) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  // ── Properties ──────────────────────────────────────────────────────

  get value(): number | null {
    return this._value;
  }

  set value(v: number | null) {
    const clean = this._cleanValue(v);
    this._value = clean;

    this._updatingFromAttr = true;
    if (clean === null) {
      this.removeAttribute('value');
    } else {
      this.setAttribute('value', String(clean));
    }
    this._updatingFromAttr = false;

    this._setFormValue(clean === null ? null : String(clean));
    this._updateDisplay();
    this._validate(this._hasShownError);
  }

  get name(): string {
    return this.getAttribute('name') || '';
  }

  set name(v: string) {
    this.setAttribute('name', v);
  }

  get label(): string {
    return this._label;
  }

  set label(v: string) {
    this.setAttribute('label', v);
  }

  get hint(): string {
    return this._hint;
  }

  set hint(v: string) {
    this.setAttribute('hint', v);
  }

  get errorText(): string {
    return this._errorText;
  }

  set errorText(v: string) {
    this.setAttribute('error-text', v);
  }

  get placeholder(): string {
    return this._placeholder;
  }

  set placeholder(v: string) {
    this.setAttribute('placeholder', v);
  }

  get size(): QdsControlSize {
    return this._size;
  }

  set size(v: QdsControlSize) {
    this.setAttribute('size', v);
  }

  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(v: boolean) {
    reflectBoolean(this, 'disabled', v);
  }

  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(v: boolean) {
    reflectBoolean(this, 'readonly', v);
  }

  get required(): boolean {
    return this._required;
  }

  set required(v: boolean) {
    reflectBoolean(this, 'required', v);
  }

  get invalid(): boolean {
    return this._invalid;
  }

  set invalid(v: boolean) {
    reflectBoolean(this, 'invalid', v);
  }

  get validateOn(): QdsValidateOn {
    return this._validateOn;
  }

  set validateOn(v: QdsValidateOn) {
    this.setAttribute('validate-on', v);
  }

  get mode(): string {
    return this._mode;
  }

  set mode(v: string) {
    this.setAttribute('mode', v);
  }

  get currency(): string {
    return this._currency;
  }

  set currency(v: string) {
    this.setAttribute('currency', v);
  }

  get locale(): string {
    return this._locale;
  }

  set locale(v: string) {
    this.setAttribute('locale', v);
  }

  get min(): number | null {
    return this._min;
  }

  set min(v: number | null) {
    if (v === null) this.removeAttribute('min');
    else this.setAttribute('min', String(v));
  }

  get max(): number | null {
    return this._max;
  }

  set max(v: number | null) {
    if (v === null) this.removeAttribute('max');
    else this.setAttribute('max', String(v));
  }

  get step(): number {
    return this._step;
  }

  set step(v: number) {
    this.setAttribute('step', String(v));
  }

  get decimals(): number | null {
    return this._decimals;
  }

  set decimals(v: number | null) {
    if (v === null) this.removeAttribute('decimals');
    else this.setAttribute('decimals', String(v));
  }

  get allowNegative(): boolean {
    return this._allowNegative;
  }

  set allowNegative(v: boolean) {
    reflectBoolean(this, 'allow-negative', v);
  }

  get negativeStyle(): string {
    return this._negativeStyle;
  }

  set negativeStyle(v: string) {
    this.setAttribute('negative-style', v);
  }

  get grouping(): boolean {
    return this._grouping;
  }

  set grouping(v: boolean) {
    reflectBoolean(this, 'grouping', v);
  }

  get steppers(): boolean {
    return this._steppers;
  }

  set steppers(v: boolean) {
    reflectBoolean(this, 'steppers', v);
  }

  get align(): string {
    return this._align;
  }

  set align(v: string) {
    this.setAttribute('align', v);
  }

  // ── Validity API ────────────────────────────────────────────────────

  get validity(): QdsValidity {
    return this._validity.validity;
  }

  get willValidate(): boolean {
    return this._internals?.willValidate ?? false;
  }

  checkValidity(): boolean {
    this._validate(true);
    try {
      return this._validity.checkValidity();
    } catch {
      return this._validity.valid;
    }
  }

  reportValidity(): boolean {
    this._hasShownError = true;
    this._validate(true);
    let valid: boolean;
    try {
      valid = this._validity.reportValidity();
    } catch {
      valid = this._validity.valid;
    }
    this._hasShownError = !valid;
    if (this.isConnected) this.render();
    return valid;
  }

  setCustomValidity(message: string): void {
    this._customValidityMessage = message;
    this._validity.setCustomValidity(message);
    this._validate(this._hasShownError);
    if (this.isConnected) this.render();
  }

  // ── Private: safe form value setter ─────────────────────────────────

  private _setFormValue(value: string | null): void {
    if (this._internals && typeof this._internals.setFormValue === 'function') {
      try {
        this._internals.setFormValue(value);
      } catch {
        /* noop — jsdom may not fully implement ElementInternals */
      }
    }
  }

  // ── Private: sync attributes to private fields ──────────────────────

  private _syncPrivateFields(): void {
    this._label = this.getAttribute('label') || '';
    this._hint = this.getAttribute('hint') || '';
    this._errorText = this.getAttribute('error-text') || '';
    this._placeholder = this.getAttribute('placeholder') || '';
    this._name = this.getAttribute('name') || '';
    this._size = parseSize(this.getAttribute('size'));
    this._disabled = isAttrTruthy(this.getAttribute('disabled'));
    this._readonly = isAttrTruthy(this.getAttribute('readonly'));
    this._required = isAttrTruthy(this.getAttribute('required'));
    this._invalid = isAttrTruthy(this.getAttribute('invalid'));
    this._validateOn = parseValidateOn(this.getAttribute('validate-on'));

    this._mode = this._parseMode(this.getAttribute('mode'));
    this._currency = this.getAttribute('currency') || 'USD';
    this._locale = this.getAttribute('locale') || '';
    this._min = this._parseNumberAttr('min');
    this._max = this._parseNumberAttr('max');
    const stepAttr = this._parseNumberAttr('step');
    this._step = stepAttr === null ? 1 : stepAttr;
    this._decimals = this._parseNumberAttr('decimals');
    this._allowNegative = this._attrBoolDefaultTrue('allow-negative');
    this._negativeStyle = this._parseNegativeStyle(this.getAttribute('negative-style'));
    this._grouping = this._attrBoolDefaultTrue('grouping');
    this._steppers = isAttrTruthy(this.getAttribute('steppers'));
    this._align = this.getAttribute('align') === 'start' ? 'start' : 'end';
  }

  // ── Private: parsing helpers ────────────────────────────────────────

  private _parseMode(v: string | null): NumericMode {
    if (v === 'currency' || v === 'percent') return v;
    return 'number';
  }

  private _parseNegativeStyle(v: string | null): NegativeStyle {
    if (v === 'parens' || v === 'parens-red') return v;
    return 'minus';
  }

  private _parseNumberAttr(name: string): number | null {
    const v = this.getAttribute(name);
    if (v === null || v === '') return null;
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  }

  private _parseAttrValue(v: string | null): number | null {
    if (v === null || v === '') return null;
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  }

  private _cleanValue(v: number | null): number | null {
    if (v === null || v === undefined) return null;
    const num = typeof v === 'number' ? v : Number(v);
    return isNaN(num) ? null : num;
  }

  private _attrBoolDefaultTrue(name: string): boolean {
    const v = this.getAttribute(name);
    return v !== null ? v !== 'false' : true;
  }

  // ── Private: locale and formatting ──────────────────────────────────

  private _getResolvedLocale(): string {
    if (this._locale) return this._locale;
    try {
      const docLang = document.documentElement.lang;
      if (docLang) return docLang;
    } catch { /* noop */ }
    try {
      if (typeof navigator !== 'undefined' && navigator.language) {
        return navigator.language;
      }
    } catch { /* noop */ }
    return 'en-US';
  }

  private _getDecimals(): number | null {
    if (this._decimals !== null) return this._decimals;
    if (this._mode === 'currency') return 2;
    if (this._mode === 'percent') return 2;
    return null;
  }

  private _getSeparators(): { decimal: string; group: string } {
    const locale = this._getResolvedLocale();
    try {
      const parts = new Intl.NumberFormat(locale).formatToParts(1234.5);
      let decimal = '.';
      let group = ',';
      for (const part of parts) {
        if (part.type === 'decimal') decimal = part.value;
        if (part.type === 'group') group = part.value;
      }
      return { decimal, group };
    } catch {
      return { decimal: '.', group: ',' };
    }
  }

  private _getCurrencyAffix(): { prefix: string; suffix: string } {
    if (this._mode !== 'currency') return { prefix: '', suffix: '' };
    const locale = this._getResolvedLocale();
    try {
      const parts = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: this._currency,
      }).formatToParts(0);

      let symbol = '';
      let symbolIndex = -1;
      let numberIndex = -1;

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (part.type === 'currency') {
          symbol = part.value;
          symbolIndex = i;
        }
        if (numberIndex === -1 && (
          part.type === 'minusSign' ||
          part.type === 'integer' ||
          part.type === 'decimal' ||
          part.type === 'fraction'
        )) {
          numberIndex = i;
        }
      }

      if (symbolIndex === -1 || numberIndex === -1) {
        return { prefix: this._currency, suffix: '' };
      }
      return symbolIndex < numberIndex
        ? { prefix: symbol, suffix: '' }
        : { prefix: '', suffix: symbol };
    } catch {
      return { prefix: this._currency, suffix: '' };
    }
  }

  private _formatDisplay(value: number | null): string {
    if (value === null) return '';

    const locale = this._getResolvedLocale();
    const decimals = this._getDecimals();
    const absValue = Math.abs(value);

    const options: Intl.NumberFormatOptions = {
      style: 'decimal',
      useGrouping: this._grouping,
    };

    if (decimals !== null) {
      options.minimumFractionDigits = decimals;
      options.maximumFractionDigits = decimals;
    }

    let formatted: string;
    try {
      formatted = new Intl.NumberFormat(locale, options).format(absValue);
    } catch {
      try {
        formatted = new Intl.NumberFormat('en-US', options).format(absValue);
      } catch {
        formatted = String(absValue);
      }
    }

    // Apply negative style
    if (value < 0) {
      if (this._negativeStyle === 'parens' || this._negativeStyle === 'parens-red') {
        formatted = `(${formatted})`;
      } else {
        formatted = `-${formatted}`;
      }
    }

    return formatted;
  }

  private _toCanonical(value: number | null): string {
    if (value === null) return '';
    return String(value);
  }

  // ── Private: parse user input ───────────────────────────────────────

  private _parseInput(raw: string): number | null {
    const trimmed = raw.trim();
    if (trimmed === '') return null;

    const { decimal, group } = this._getSeparators();
    let s = trimmed;

    // Handle parentheses for negative
    const parensMatch = s.match(/^\((.+)\)$/);
    if (parensMatch) {
      s = '-' + parensMatch[1].trim();
    }

    // Strip spaces
    s = s.replace(/\s/g, '');

    // Strip grouping separators using locale-specific separator
    if (group) {
      const escapedGroup = group.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      s = s.replace(new RegExp(escapedGroup, 'g'), '');
    }

    // Convert locale decimal to canonical '.'
    if (decimal && decimal !== '.') {
      const escapedDecimal = decimal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      s = s.replace(new RegExp(escapedDecimal, 'g'), '.');
    }

    // Strip any remaining non-numeric characters (currency symbols etc)
    s = s.replace(/[^0-9.\-]/g, '');

    if (s === '' || s === '-' || s === '.' || s === '-.') return null;

    const num = parseFloat(s);
    return isNaN(num) ? null : num;
  }

  // ── Private: paste sanitisation ─────────────────────────────────────

  private _sanitizePaste(raw: string): string | null {
    let s = raw.trim();
    if (s === '') return null;

    // Handle parentheses for negative: (1,234) → -1234
    const parensMatch = s.match(/^\((.+)\)$/);
    if (parensMatch) {
      s = '-' + parensMatch[1].trim();
    }

    const { decimal, group } = this._getSeparators();

    // Strip grouping separators
    if (group) {
      const escapedGroup = group.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      s = s.replace(new RegExp(escapedGroup, 'g'), '');
    }

    // Convert locale decimal to canonical '.'
    if (decimal && decimal !== '.') {
      const escapedDecimal = decimal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      s = s.replace(new RegExp(escapedDecimal, 'g'), '.');
    }

    // Strip spaces and currency symbols — keep only digits, '.', '-'
    s = s.replace(/[^0-9.\-]/g, '');

    if (s === '' || s === '-' || s === '.' || s === '-.') return null;

    const num = parseFloat(s);
    if (isNaN(num)) return null;

    // Reject negative when not allowed
    if (!this._allowNegative && num < 0) return null;

    return s;
  }

  // ── Private: key filtering ──────────────────────────────────────────

  private _isKeyAllowed(key: string, e: KeyboardEvent): boolean {
    // Navigation keys
    const navKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'];
    if (navKeys.includes(key)) return true;

    // Clipboard / edit shortcuts (Ctrl/Cmd+a/c/v/x/z/y)
    if (e.ctrlKey || e.metaKey) {
      if (['a', 'c', 'v', 'x', 'z', 'y'].includes(key.toLowerCase())) return true;
    }

    // Digits
    if (/^[0-9]$/.test(key)) return true;

    // Minus sign
    if (key === '-' && this._allowNegative) return true;

    // Decimal separator: allow both locale decimal and canonical '.'
    const { decimal } = this._getSeparators();
    if (key === '.') return true;
    if (decimal && key === decimal) return true;

    return false;
  }

  // ── Private: rounding ───────────────────────────────────────────────

  private _roundHalfUp(value: number, decimals: number): number {
    const safeDecimals = Math.max(0, decimals);
    const factor = Math.pow(10, safeDecimals);
    const absVal = Math.abs(value);
    // Shift, round (half-up = away from zero for negatives), unshift
    const rounded = Math.round(absVal * factor * (1 + Number.EPSILON)) / factor;
    return value < 0 ? -rounded : rounded;
  }

  // ── Private: stepping ───────────────────────────────────────────────

  private _stepValue(multiplier: number): void {
    const step = this._step * multiplier;
    const current = this._value ?? 0;
    let next = current + step;

    // Clamp to min/max
    if (this._max !== null && next > this._max) next = this._max;
    if (this._min !== null && next < this._min) next = this._min;

    this._commitStepped(next);
  }

  private _commitStepped(next: number): void {
    this._value = next;
    this._updatingFromAttr = true;
    this.setAttribute('value', String(next));
    this._updatingFromAttr = false;
    this._setFormValue(String(next));
    this._updateDisplay();
    this._validate(this._hasShownError);
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  }

  // ── Private: commit from input (blur / Enter) ───────────────────────

  private _commitFromInput(): void {
    const input = this._getInput();
    if (!input) return;

    const parsed = this._parseInput(input.value);
    const decimals = this._getDecimals();
    const rounded = parsed !== null && decimals !== null
      ? this._roundHalfUp(parsed, decimals)
      : parsed;

    this.value = rounded;

    if (this._validateOn === 'blur') {
      this._hasShownError = true;
      this._validate(true);
    }
  }

  // ── Private: validation ─────────────────────────────────────────────

  private _validate(showError: boolean): void {
    const flags: Partial<QdsValidity> = {
      valid: true,
      valueMissing: false,
      typeMismatch: false,
      patternMismatch: false,
      rangeUnderflow: false,
      rangeOverflow: false,
      stepMismatch: false,
      customError: false,
    };

    let message = '';

    if (this._required && this._value === null) {
      flags.valueMissing = true;
      flags.valid = false;
      message = 'Please enter a value.';
    }

    if (this._value !== null) {
      if (this._min !== null && this._value < this._min) {
        flags.rangeUnderflow = true;
        flags.valid = false;
        message = `Value must be at least ${this._min}.`;
      }
      if (this._max !== null && this._value > this._max) {
        flags.rangeOverflow = true;
        flags.valid = false;
        message = `Value must be at most ${this._max}.`;
      }
      if (this._step !== null && this._step > 0) {
        const base = this._min ?? 0;
        const remainder = (this._value - base) / this._step;
        const nearest = Math.round(remainder);
        if (Math.abs(remainder - nearest) > 1e-9) {
          flags.stepMismatch = true;
          flags.valid = false;
          message = `Value must be a multiple of ${this._step}.`;
        }
      }
    }

    if (this._customValidityMessage) {
      flags.customError = true;
      flags.valid = false;
      message = this._customValidityMessage;
    }

    this._validity.setValidity(flags, message);

    const valid: boolean = flags.valid ?? true;
    if (valid !== this._prevValid || message !== this._prevMessage) {
      this._prevValid = valid;
      this._prevMessage = message;
      dispatchValidityChange(this, this._validity);
    }

    if (showError || (this._hasShownError && this._validateOn !== 'submit')) {
      // Sticky: once the error UI has been shown, it stays "shown" for the
      // rest of this interaction (until formResetCallback), so later input
      // events keep re-validating live. Do NOT tie this to current validity —
      // that previously caused live re-validation to stop firing the moment
      // the value went back in range.
      this._hasShownError = true;
      if (!valid) {
        this.setAttribute('data-invalid', '');
      } else {
        this.removeAttribute('data-invalid');
      }
    }
  }

  // ── Private: dispatch change ────────────────────────────────────────

  private _dispatchChange(): void {
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  // ── Private: DOM helpers ────────────────────────────────────────────

  private _getInput(): HTMLInputElement | null {
    return (this.shadowRoot?.querySelector(`#${this._inputId}`) as HTMLInputElement) || null;
  }

  private _updateDisplay(): void {
    const input = this._getInput();
    if (!input) return;
    if (this._isFocused) {
      input.value = this._toCanonical(this._value);
    } else {
      input.value = this._formatDisplay(this._value);
    }
  }

  private _escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private _attachListeners(): void {
    const input = this._getInput();
    if (input) {
      input.addEventListener('focus', this._onFocus);
      input.addEventListener('blur', this._onBlur);
      input.addEventListener('input', this._onInput);
      input.addEventListener('keydown', this._onKeyDown);
      input.addEventListener('paste', this._onPaste);
    }
    const stepperUp = this.shadowRoot?.querySelector('#stepper-up');
    if (stepperUp) stepperUp.addEventListener('click', this._onStepperUp);
    const stepperDown = this.shadowRoot?.querySelector('#stepper-down');
    if (stepperDown) stepperDown.addEventListener('click', this._onStepperDown);
  }

  private _detachListeners(): void {
    const input = this._getInput();
    if (input) {
      input.removeEventListener('focus', this._onFocus);
      input.removeEventListener('blur', this._onBlur);
      input.removeEventListener('input', this._onInput);
      input.removeEventListener('keydown', this._onKeyDown);
      input.removeEventListener('paste', this._onPaste);
    }
    const stepperUp = this.shadowRoot?.querySelector('#stepper-up');
    if (stepperUp) stepperUp.removeEventListener('click', this._onStepperUp);
    const stepperDown = this.shadowRoot?.querySelector('#stepper-down');
    if (stepperDown) stepperDown.removeEventListener('click', this._onStepperDown);
  }

  // ── Private: error display computed state ───────────────────────────

  private get _showErrorState(): boolean {
    if (this._invalid && this._errorText !== '') return true;
    if (this._hasShownError && !this._validity.valid && this._validity.message !== '') return true;
    return false;
  }

  private get _errorDisplayText(): string {
    if (this._invalid && this._errorText !== '') return this._errorText;
    if (this._hasShownError && !this._validity.valid) return this._validity.message;
    return '';
  }

  // ── Private: styles ─────────────────────────────────────────────────

  private _getStyles(): string {
    return `
      ${getFormControlStyles()}

      :host {
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
      }

      .qds-numeric-container {
        display: flex;
        flex-direction: column;
      }

      .qds-control-surface {
        padding: 0 var(--theme-space-3, 12px);
      }
      :host([size="lg"]) .qds-control-surface {
        padding: 0 var(--theme-space-4, 16px);
      }

      .qds-affix {
        display: flex;
        align-items: center;
        color: var(--theme-text-secondary, #555555);
        font-size: 14px;
        flex-shrink: 0;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
        user-select: none;
      }

      .qds-affix-prefix {
        margin-right: var(--theme-space-1, 4px);
      }

      .qds-affix-suffix {
        margin-left: var(--theme-space-1, 4px);
      }

      .qds-input {
        flex: 1;
        min-width: 0;
        border: none;
        background: transparent;
        color: inherit;
        font: inherit;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
        padding: 0;
        outline: none;
        text-align: right;
      }
      :host([align="start"]) .qds-input {
        text-align: left;
      }

      .qds-input::placeholder {
        color: var(--theme-text-tertiary, #6A6A6A);
      }

      .qds-input:disabled {
        cursor: not-allowed;
      }

      .qds-input[readonly] {
        cursor: default;
      }

      .parens-red .qds-input {
        color: var(--theme-error, #8B1F00);
      }

      .qds-steppers {
        display: flex;
        flex-direction: column;
        height: var(--qds-control-height, 40px);
        margin-left: var(--theme-space-1, 4px);
        border-left: 1px solid var(--theme-border-primary, #E4E9F2);
        overflow: hidden;
        flex-shrink: 0;
      }

      .qds-stepper-btn {
        flex: 1;
        border: none;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--theme-text-tertiary, #6A6A6A);
        font-size: 8px;
        padding: 0;
        min-height: 20px;
        transition: background-color var(--transition-speed-fast, 0.15s),
                    color var(--transition-speed-fast, 0.15s);
      }

      .qds-stepper-btn:hover {
        background: var(--button-hover-bg, rgba(0, 0, 0, 0.05));
        color: var(--theme-text-primary, #333333);
      }

      .qds-stepper-btn:active {
        background: var(--theme-background-tertiary, #E9ECEF);
      }

      .qds-stepper-btn:first-child {
        border-bottom: 1px solid var(--theme-border-primary, #E4E9F2);
      }

      @media (pointer: coarse) {
        .qds-control-surface.with-steppers {
          flex-wrap: wrap;
          height: auto;
          align-items: stretch;
        }
        .qds-control-surface.with-steppers .qds-input,
        .qds-control-surface.with-steppers .qds-affix {
          align-self: center;
        }
        .qds-steppers {
          flex-direction: row;
          width: 100%;
          height: auto;
          margin-left: 0;
          border-left: none;
          border-top: 1px solid var(--theme-border-primary, #E4E9F2);
        }
        .qds-stepper-btn {
          min-height: 44px;
          min-width: 44px;
          flex: 1;
          font-size: 12px;
        }
        .qds-stepper-btn:first-child {
          border-bottom: none;
          border-right: 1px solid var(--theme-border-primary, #E4E9F2);
        }
      }
    `;
  }

  // ── Private: template ───────────────────────────────────────────────

  private _template(): string {
    const showLabel = this._label !== '';
    const showRequired = this._required;
    const showHint = this._hint !== '' && !this._showErrorState;
    const showError = this._showErrorState;

    const affix = this._getCurrencyAffix();
    const prefix = this._mode === 'currency' ? affix.prefix : '';
    const suffix = affix.suffix || (this._mode === 'percent' ? '%' : '');

    const isInvalid = this._invalid || (this._hasShownError && !this._validity.valid);
    const displayValue = this._isFocused
      ? this._toCanonical(this._value)
      : this._formatDisplay(this._value);

    const negativeClass =
      this._value !== null &&
      this._value < 0 &&
      this._negativeStyle === 'parens-red' &&
      !this._isFocused
        ? 'parens-red'
        : '';

    const wrapperClasses = [
      'qds-control-surface',
      this._steppers ? 'with-steppers' : '',
      negativeClass,
    ].filter(Boolean).join(' ');

    const stepperHtml = this._steppers ? `
      <div class="qds-steppers">
        <button part="stepper-up" id="stepper-up" class="qds-stepper-btn"
          aria-label="Increment" tabindex="-1" type="button">&#9650;</button>
        <button part="stepper-down" id="stepper-down" class="qds-stepper-btn"
          aria-label="Decrement" tabindex="-1" type="button">&#9660;</button>
      </div>
    ` : '';

    const prefixHtml = prefix
      ? `<span part="affix-prefix" class="qds-affix qds-affix-prefix">${this._escapeHtml(prefix)}</span>`
      : '';

    const suffixHtml = suffix
      ? `<span part="affix-suffix" class="qds-affix qds-affix-suffix">${this._escapeHtml(suffix)}</span>`
      : '';

    const describedBy = [
      showHint ? this._hintId : '',
      showError ? this._errorId : '',
    ].filter(Boolean).join(' ');

    const inputAttrs = [
      `id="${this._inputId}"`,
      'part="input"',
      'class="qds-input"',
      'type="text"',
      'inputmode="decimal"',
      `value="${this._escapeHtml(displayValue)}"`,
      this._placeholder ? `placeholder="${this._escapeHtml(this._placeholder)}"` : '',
      this._name ? `name="${this._escapeHtml(this._name)}"` : '',
      this._disabled ? 'disabled' : '',
      this._readonly ? 'readonly' : '',
      isInvalid ? 'aria-invalid="true"' : '',
      this._required ? 'aria-required="true"' : '',
      describedBy ? `aria-describedby="${describedBy}"` : '',
      'autocomplete="off"',
      'spellcheck="false"',
    ].filter(Boolean).join(' ');

    return `
      <style>${this._getStyles()}</style>
      <div part="container" class="qds-numeric-container">
        ${showLabel ? `
          <label part="label" class="qds-label" for="${this._inputId}">
            ${this._escapeHtml(this._label)}${showRequired ? ' <span part="required" class="qds-required-marker" aria-hidden="true">*</span>' : ''}
          </label>
        ` : ''}
        <div part="wrapper" class="${wrapperClasses}">
          ${prefixHtml}
          <input ${inputAttrs} />
          ${suffixHtml}
          ${stepperHtml}
        </div>
        ${showHint ? `<div part="hint" class="qds-hint" id="${this._hintId}">${this._escapeHtml(this._hint)}</div>` : ''}
        ${showError ? `<div part="error" class="qds-error" id="${this._errorId}" role="alert" aria-live="polite">${this._escapeHtml(this._errorDisplayText)}</div>` : ''}
      </div>
    `;
  }

  // ── Render ──────────────────────────────────────────────────────────

  private render(): void {
    if (!this.shadowRoot) return;
    this._detachListeners();
    this.shadowRoot.innerHTML = this._template();
    this._attachListeners();
  }
}

if (!customElements.get('qds-numeric-input')) {
  customElements.define('qds-numeric-input', QdsNumericInput);
}
